import { createSelector, OutputSelectorFields } from 'reselect'
import get from 'lodash/get'
import partialRight from 'lodash/partialRight'
import orderBy from 'lodash/orderBy'
import moment from 'moment'
import { Finance } from '../../quartermaster/src'
import { selectLoadingState } from './client'
import { selectFromBuyerInfo } from './buyerInfo'
import { Prequal } from '../../types/prequal'
import { RootState } from '../../types/rootState'

// TODO: TS state interface
type State = any

export const selectLoading = selectLoadingState

export const selectPrequalifications = selectFromBuyerInfo('prequalifications')

export const selectHasPrequal = createSelector(selectPrequalifications, (prequals: Prequal[]) =>
  Boolean((prequals || []).length)
)

export const selectFirstPrequal = createSelector(selectPrequalifications, (prequals: Prequal[]) =>
  prequals && prequals.length > 0 ? prequals[0] : undefined
)

export const getFromPrequal =
  <T>(path: string) =>
  (prequal: Prequal): T =>
    get(prequal, path)

const getMomentIfValid = (date: string) => {
  const m = moment(date)
  return m.isValid() ? m : null
}

const selectValidMomentFromPrequal = (path: string) => createSelector(getFromPrequal(path), getMomentIfValid)

export const selectRate = createSelector(selectFirstPrequal, (prequal: Prequal | undefined) =>
  prequal ? parseFloat(getFromPrequal<string>('apr')(prequal)) : 0
)
export const selectTermMonths = getFromPrequal('loanTermMonths')
export const selectType = getFromPrequal<string>('loanType')
export const selectCreatedAt = getFromPrequal<string>('createdAt')
export const selectUpdatedAt = selectValidMomentFromPrequal('updatedAt')
export const selectExpiresAt = selectValidMomentFromPrequal('expiresAt')
export const selectAdjustable = getFromPrequal<boolean>('loanAdjustable')
export const selectArmYearsInitial = getFromPrequal<number>('loanArmYearsInitial')
export const selectMaxLoanAmount = getFromPrequal<number>('maxLoanAmount')
export const selectPrequalId = getFromPrequal<string>('preapproved')
export const selectMaxHomePrice = getFromPrequal<number>('maxHomePrice')
export const selectDownpayment = getFromPrequal<number>('downpayment')
export const selectAmount = selectMaxHomePrice
export const selectTermYears = createSelector(selectTermMonths, (months: number) => months / 12)
export const selectTerm = createSelector(
  selectAdjustable,
  selectArmYearsInitial,
  selectTermYears,
  (adjustable: boolean, armYearsInitial: number, termYears: number) => ({
    adjustable,
    years: adjustable ? armYearsInitial : termYears
  })
)

export const selectMonthlyPayment = createSelector(
  selectAmount,
  selectTermMonths,
  selectRate,
  (amount: number, months: number, rate: number) =>
    amount && months && rate && Finance.calculatePayment(amount, months, rate)
)

export const selectNewestPrequal = createSelector(selectPrequalifications, (prequals: Prequal[]) => {
  if (!prequals || prequals.length === 0) {
    return undefined
  }

  const sortedPrequals = orderBy(prequals, selectCreatedAt, ['desc'])
  return sortedPrequals[0]
})

export const selectNewestPrequalAmount = createSelector(selectNewestPrequal, selectAmount)
